import type { CardStandard, LessonStandard } from "@shared/models/card-stack"
import {
  StandardContentExtension,
  type ExtensionStandardAttrs,
} from "@tiptap-content/extensions/standard/standard-content-extension"
import type { Node as PMNode } from "@tiptap/pm/model"
import { ReactNodeViewRenderer } from "@tiptap/react"
import DOMPurify from "dompurify"
import { StandardComponent } from "./standard-component"

/**
 * Collect every standard currently in the lesson, deduplicated by id (a standard can appear
 * in more than one card).
 */
export function extractLessonStandards(doc: PMNode): Array<LessonStandard> {
  const standards: Array<LessonStandard> = []
  const seenIds = new Set<string>()
  doc.descendants((node) => {
    if (node.type.name === "standard") {
      const { standard } = node.attrs as ExtensionStandardAttrs
      if (!seenIds.has(standard.id)) {
        seenIds.add(standard.id)
        standards.push(standard)
      }
    }
  })
  return standards
}

/**
 * The statement's plain text, with any markup stripped. Sanitized first so a statement
 * carrying an injected node contributes only its visible text — the same treatment the
 * rendered statement gets in standard-component.
 */
export function statementText(html: string): string {
  return DOMPurify.sanitize(html, { RETURN_DOM: true }).textContent ?? ""
}

type EmphasisLevel = CardStandard["emphasisLevel"]

/**
 * Where a click on the row leaves it: open rows close, closed rows open. A starred standard is
 * already open and stays starred — the level it returns is the one it was given, which is how the
 * row's handler knows there is nothing to commit.
 */
export function emphasisAfterRowClick(level: EmphasisLevel): EmphasisLevel {
  if (level === "STARRED") return "STARRED"
  return level === "EXPANDED" ? null : "EXPANDED"
}

/** Where a click on the star leaves it. Unstarring closes the row: STARRED is EXPANDED plus a star. */
export function emphasisAfterStarClick(level: EmphasisLevel): EmphasisLevel {
  return level === "STARRED" ? null : "STARRED"
}

export const ExtensionStandard = StandardContentExtension.extend({
  // Plain-text rendering (getText and the clipboard's text/plain flavor); without
  // this, a standard contributes nothing since all its data lives in attrs.
  renderText({ node }) {
    const { standard, emphasisLevel } = node.attrs as ExtensionStandardAttrs
    const star = emphasisLevel === "STARRED" ? "★ " : ""
    return `${star}${standard.code ?? ""} ${statementText(standard.statement)} (${standard.jurisdiction}. ${standard.subject}. ${standard.grades}.)`
  },

  addNodeView() {
    return ReactNodeViewRenderer(StandardComponent)
  },
})
